import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams, IRowNode } from 'ag-grid-community';


import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Signal } from '@angular/core';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';

import { ApmTranslatePipe, DomainDataFacade, PermissionUtils } from '@mitsubishi/core';
import { PriceListSubfamily } from '@mitsubishi/pricing-list/data-access';
import { SegmentBandComponent } from '@mitsubishi/shared/components';
import { Brand } from '@mitsubishi/shared/data-access';
import { IconComponent, SelectModule } from '@mitsubishi/shared/ui';

interface PriceListSubfamilyCellRendererParams extends ICellRendererParams {
  actions?: any;
  showAlerts?: boolean;
  hideActions?: boolean;
  explicitLevel?: number;
  hideSegmentBorder?: boolean;
  organization?: Signal<{
    id: number;
    name: string;
  }>;
}

@Component({
  selector: 'apm-price-list-subfamily-group-cell',
  templateUrl: './price-list-subfamily-group-cell.component.html',
  styleUrls: ['./price-list-subfamily-group-cell.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SegmentBandComponent,
    ApmTranslatePipe,
    IconComponent,
    MatMenu,
    MatMenuItem,
    SelectModule,
    MatMenuTrigger,
    MatTooltip
],
})
export class PriceListSubfamilyGroupCellComponent implements ICellRendererAngularComp {
  rowData!: PriceListSubfamily;
  node!: IRowNode<PriceListSubfamily>;

  canViewDownloadPriceList = PermissionUtils.hasViewDownloadPriceListPermission();
  canViewMarkup = PermissionUtils.hasViewMarkupPermission();
  canManageExtendedWarranties = PermissionUtils.hasManageExtendedWarrantiesPermission();

  level = 0;
  allChildrenCount = 0;
  hideActions = false;
  showAlerts = false;
  hasError = false;
  hasConflict = false;
  hideSegmentBorder = false;
  actions: any;

  organization?: Signal<{ name: string; id: number } | undefined>;

  groupDescriptionId: number | undefined;
  brand: Brand | undefined;

  constructor(
    private cdr: ChangeDetectorRef,
    private sharedDataFacade: DomainDataFacade
  ) {}

  agInit(params: ICellRendererParams) {
    this.setData(params);
  }

  refresh(params: ICellRendererParams): boolean {
    this.setData(params);

    return true;
  }

  openChangelogDialog() {
    if (this.organization === undefined) return; // non posso usare !orgId perchè potrebbe essere 0

    const rows = this.node.allLeafChildren.map(row => {
      return { subfamilyId: row.data?.subfamilyId, familySapCode: row.data?.familySAPCode };
    });
    this.actions.openChangelog(rows);
  }

  exportPriceList() {
    const nodeClassId = this.getNodeClassId(this.node);
    this.actions.exportPriceList(this.getSubfamilyIds(this.node), nodeClassId);
  }

  exportMarkup() {
    this.actions.exportMarkup(this.getSubfamilySapCodePairs(this.node));
  }

  exportExtendedWarranties() {
    this.actions.exportExtendedWarranties(this.getSubfamilyIds(this.node));
  }

  exportExtendedWarrantiesMarkup() {
    this.actions.exportExtendedWarrantiesMarkup(this.getSubfamilySapCodePairs(this.node));
  }

  private setData(params: PriceListSubfamilyCellRendererParams) {
    this.node = params.node;
    this.level = params.explicitLevel ?? params.node.level;
    this.actions = params.actions;
    this.hideActions = params.hideActions ?? false;
    this.showAlerts = params.showAlerts ?? false;
    this.hideSegmentBorder = params.hideSegmentBorder ?? false;
    this.organization = params.organization;

    if (params.node.group) {
      this.allChildrenCount = params.node.allChildrenCount ?? 0;
      this.hasError = params.node.allLeafChildren.some(child => child.data?.changeType === 'error');
      this.hasConflict = params.node.allLeafChildren.some(child => child.data?.changeType === 'conflict');

      const firstChild = params.node.allLeafChildren[0];
      this.setDataFromNode(firstChild);
    } else {
      this.allChildrenCount = 1;
      this.hasError = params.data?.changeType === 'error';
      this.hasConflict = params.data?.changeType === 'conflict';

      this.setDataFromNode(params.node);
    }

    this.cdr.detectChanges();
  }

  private setDataFromNode(node: IRowNode<PriceListSubfamily> | undefined) {
    if (!node || !node.data) {
      return;
    }

    this.rowData = node.data;
    this.brand = this.sharedDataFacade.getBrandById(this.rowData.brandId);
    if (this.level === 0) {
      this.groupDescriptionId = this.rowData.classDescriptionId;
    }
    if (this.level === 1) {
      this.groupDescriptionId = this.rowData.subclassDescriptionId;
    }
  }

  private getSubfamilyIds(node: IRowNode<PriceListSubfamily>): number[] {
    if (node.group && node.childrenAfterFilter) {
      let subfamilyIds: number[] = [];
      for (const childNode of node.childrenAfterFilter) {
        subfamilyIds = subfamilyIds.concat(this.getSubfamilyIds(childNode));
      }
      return subfamilyIds;
    }

    return node.data ? [node.data.subfamilyId] : [];
  }

  private getSubfamilySapCodePairs(node: IRowNode<PriceListSubfamily>): Array<{ subfamilyId: number; familySapCode: string }> {
    if (node.group && node.childrenAfterFilter) {
      let pairs: Array<{ subfamilyId: number; familySapCode: string }> = [];
      for (const childNode of node.childrenAfterFilter) {
        pairs = pairs.concat(this.getSubfamilySapCodePairs(childNode));
      }
      return pairs;
    }

    return node.data ? [{ subfamilyId: node.data.subfamilyId, familySapCode: node.data.familySAPCode }] : [];
  }

  private getNodeClassId(node: IRowNode<PriceListSubfamily>): number | undefined {
    if (!node.group) {
      return node.data?.classId;
    }

    return node.allLeafChildren ? node.allLeafChildren[0]?.data?.classId : undefined;
  }
}
